import { useMemo, useState } from "react";
import { FiHelpCircle, FiChevronDown, FiChevronUp } from "react-icons/fi";

export default function Faq() {
  const sections = useMemo(
    () => [
      {
        id: "team-selection",
        title: "Team Selection",
        description:
          "How Distributely decides which team should receive an assignment before selecting an owner.",
        items: [
          {
            q: "What is Team Selection?",
            a: `Team Selection determines which team should be eligible to receive an assignment.
Once the eligible team (or teams) are determined, Distributely then assigns an owner from those teams.`
          },
          {
            q: "What Makes a Team Active?",
            a: `In order to be considered active and eligible for distributions, a team must have the 'active' flag set to true, must have at least one active member, must have at least one rule and condition, and must have either "mock" or "live" routing enables, or both.`
          },
          {
            q: "What happens if Team Rules are enabled?",
            a: `If Team Rules are enabled, Distributely evaluates the rules for each team to determine eligibility.
Only teams whose rules match the record will be considered for assignment.`
          },
          {
            q: "What happens if no Team Rules match?",
            a: `If no rules match, Distributely falls back based on your configured behavior.
Most commonly, it assigns using the first eligible default team or the first active team in priority order.`
          },
          {
            q: "What does Team Order / Priority affect?",
            a: `Team Order controls the order in which teams are evaluated and attempted.
Lower order values (higher priority) are processed first.`
          }
        ]
      },
      {
        id: "owner-selection",
        title: "Owner Selection",
        description:
          "How Distributely selects a team member once a team (or teams) has been chosen.",
        items: [
          {
            q: "What is Owner Selection?",
            a: `Owner Selection determines which team member receives the assignment once a team has been selected.
Owner Selection methods include round robin, weighted selection, and attribute-based selection.`
          },
          {
            q: "What happens if a team has no available members?",
            a: `Behavior depends on each team's fallback setting:
- Skip to the next eligible team
- Assign to a designated fallback owner
- Ignore availability and use round robin across active members
If no teams can assign, Distributely can fall back to the portal-level default owner (if configured).`
          },
          {
            q: "What makes a member 'available'?",
            a: `A member must be active and meet availability rules such as:
- not on time off
- not outside working hours
- not marked unavailable
- not over their cap (if caps are configured)
Distributely only assigns to members that meet the eligibility requirements.`
          },
          {
            q: "Can Distributely skip ineligible members?",
            a: `Yes. Certain routing methods (such as Attribute-Based Skip routing) maintain the full round robin order,
but skip members who do not match the required attribute.`
          }
        ]
      },
      {
        id: "routing",
        title: "Routing & Weights",
        description:
          "How routing methods work, and how weights influence assignments over time.",
        items: [
          {
            q: "What is Round Robin routing?",
            a: `Round Robin assigns the next eligible team member in a rotating order.
It helps distribute assignments evenly across all active and available members.`
          },
          {
            q: "What is Weighted Round Robin routing?",
            a: `Weighted routing assigns owners similarly to round robin, but each member receives assignments proportional to their weight.
A higher weight means that person is more likely to be selected over time.`
          },
          {
            q: "What is Attribute-Based Round Robin routing?",
            a: `Attribute-Based routing filters members by a required attribute (for example: certification, language, territory).
Then it performs round robin among the matching members.
If no members match, it will fall back to standard round robin (based on your configured behavior).`
          },
          {
            q: "What is Attribute-Based Round Robin (Skip)?",
            a: `This mode keeps everyone in the round robin order but skips members who do not match the required attribute.
If no team has a matching member, Distributely can fall back to standard round robin on the first eligible team.`
          },
          {
            q: "What does a member weight do?",
            a: `A member weight affects how often that member receives assignments compared to others.
Example: If one member has weight 2 and another has weight 1, the first member should receive about twice as many assignments over time.`
          },
          {
            q: "What happens if a member weight is blank?",
            a: `If weight is blank or null, the system typically treats that member as default weight (usually 1).
Exact behavior depends on how your portal is configured.`
          }
        ]
      },
      {
        id: "member-caps",
        title: "Member Caps",
        description:
          "How caps limit assignments by time period, team scope, and optional dimensions.",
        items: [
          {
            q: "What are Member Caps?",
            a: `Member Caps limit how many assignments a member can receive within a time period.
Caps can be global (across all teams) or scoped to a single team.`
          },
          {
            q: "What happens when a member hits a cap?",
            a: `Caps are hard limits. Once a member reaches their cap, they are not eligible to receive additional assignments for that period.`
          },
          {
            q: "What are dimension-based caps?",
            a: `Dimension caps apply only when a HubSpot property matches a configured value.
Example: source = facebook. If the property is missing or empty, the dimension cap does not apply.`
          },
          {
            q: "How do 'all' caps work?",
            a: `If no dimension property is set, the cap applies to all assignments within the period.
If both an "all" cap and a dimension cap exist, both are enforced.`
          }
        ]
      }
    ],
    []
  );

  const [openItem, setOpenItem] = useState(null);

  const toggleItem = (sectionId, index) => {
    const key = `${sectionId}:${index}`;
    setOpenItem((prev) => (prev === key ? null : key));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <FiHelpCircle className="w-6 h-6 text-gray-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">FAQ</h1>
        </div>
        <p className="text-gray-600">
          Answers to common questions about team selection, owner selection, routing methods, weights, and assignment behavior.
        </p>
      </div>

      {/* Overview Panel */}
      <div className="mb-10 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          FAQ Overview
        </h3>
        <p className="text-sm text-blue-700">
          This page explains how Distributely selects teams and owners, how routing rules work,
          and how weights influence assignment behavior.
        </p>
      </div>

      {/* FAQ Sections */}
      <div className="space-y-10">
        {sections.map((section) => (
          <div
            key={section.id}
            className="bg-white rounded-xl shadow-sm border border-gray-200"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {section.title}
              </h2>
              <p className="text-sm text-gray-600 mt-1">{section.description}</p>
            </div>

            <div className="divide-y divide-gray-200">
              {section.items.map((item, idx) => {
                const key = `${section.id}:${idx}`;
                const isOpen = openItem === key;

                return (
                  <div key={key} className="px-6 py-4">
                    <button
                      onClick={() => toggleItem(section.id, idx)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <span className="text-sm font-semibold text-gray-900">
                        {item.q}
                      </span>
                      {isOpen ? (
                        <FiChevronUp className="w-4 h-4 text-gray-600" />
                      ) : (
                        <FiChevronDown className="w-4 h-4 text-gray-600" />
                      )}
                    </button>

                    {isOpen && (
                      <div className="mt-3 text-sm text-gray-700 whitespace-pre-line">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

